import React, { useState } from 'react';
import { StoreCart } from '../types';
import { formatCurrency } from '../util/formatCurrency';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { toast } from 'react-toastify';

type StoreCartModalProps = {
  isOpen: boolean;
  onClose: () => void;
  storeCart: StoreCart;
  orderId: number;
  orderStatus: string;
  onConfirmed?: () => void;
};

const StoreCartModal: React.FC<StoreCartModalProps> = ({ isOpen, onClose, storeCart, orderId, orderStatus, onConfirmed }) => {
  const [quantities, setQuantities] = useState<Record<number, number>>(
    Object.fromEntries(storeCart.store_cart_order_items.map((item) => [item.id, item.quantity]))
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);

  if (!isOpen) return null;

  const canConfirm = orderStatus === 'submitted';
  const totalItems = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

  const handleIncrement = (itemId: number) =>
    setQuantities((prev) => ({ ...prev, [itemId]: (prev[itemId] ?? 1) + 1 }));

  const handleDecrement = (itemId: number) =>
    setQuantities((prev) => ({ ...prev, [itemId]: Math.max(1, (prev[itemId] ?? 1) - 1) }));

  const handleConfirmClick = () => {
    if (storeCart.cart_confirmed) {
      setShowOverrideWarning(true);
    } else {
      submitConfirmation();
    }
  };

  const submitConfirmation = async () => {
    setIsConfirming(true);
    setShowOverrideWarning(false);
    try {
      const quantitiesPayload: Record<string, number> = {};
      storeCart.store_cart_order_items.forEach((item) => {
        quantitiesPayload[item.id.toString()] = quantities[item.id] ?? item.quantity;
      });

      const res = await fetchWithAuth(`/orders/${orderId}/store_cart/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantities: quantitiesPayload }),
      });

      if (res.ok) {
        toast.success('Cart confirmed successfully.');
        onConfirmed?.();
        onClose();
      } else {
        const data = await res.json();
        toast.error(data.error ?? 'Failed to confirm cart.');
      }
    } catch {
      toast.error('An error occurred while confirming the cart.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-md shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-bold">{storeCart.store.name} Cart Items</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className="overflow-y-auto p-4">
          {storeCart.cart_confirmed && (
            <p className="text-green-700 text-sm mb-3 font-medium">This cart has already been confirmed by the customer.</p>
          )}
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b font-semibold text-gray-700">
                <th className="py-2 pr-4">Item Description</th>
                <th className="py-2 pr-4 text-center">Qty</th>
                <th className="py-2 text-right">Unit Price</th>
              </tr>
            </thead>
            <tbody>
              {storeCart.store_cart_order_items.map((item) => (
                <tr key={item.id} className="border-b last:border-0">
                  <td className="py-2 pr-4">{item.item_description}</td>
                  <td className="py-2 pr-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {canConfirm && (
                        <button
                          onClick={() => handleDecrement(item.id)}
                          disabled={(quantities[item.id] ?? item.quantity) <= 1}
                          className="w-7 h-7 rounded border flex items-center justify-center text-lg font-medium disabled:opacity-30 hover:bg-gray-100"
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                      )}
                      <span className="w-8 text-center text-sm font-medium tabular-nums">
                        {quantities[item.id] ?? item.quantity}
                      </span>
                      {canConfirm && (
                        <button
                          onClick={() => handleIncrement(item.id)}
                          className="w-7 h-7 rounded border flex items-center justify-center text-lg font-medium hover:bg-gray-100"
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-2 text-right">{formatCurrency(item.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {showOverrideWarning && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded text-sm text-yellow-800">
              <p className="font-medium mb-2">The customer has already confirmed this cart. Are you sure you want to update it?</p>
              <div className="flex gap-2">
                <button
                  onClick={submitConfirmation}
                  className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
                >
                  Yes, update
                </button>
                <button
                  onClick={() => setShowOverrideWarning(false)}
                  className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-between items-center p-4 border-t">
          <span className="text-sm text-gray-600">
            Total: <strong>{totalItems} item{totalItems !== 1 ? 's' : ''}</strong>
          </span>
          <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          >
            Close
          </button>
          {canConfirm && !showOverrideWarning && (
            <button
              onClick={handleConfirmClick}
              disabled={isConfirming}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {isConfirming ? 'Confirming...' : 'Confirm Cart'}
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreCartModal;
